import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getQuotesApi,
  deleteQuoteApi,
  getQuoteByIdApi,
  createQuoteApi,
  updateStatusApi,
  editQuoteApi,
  delteAttachmentApi,
  getDuplicateQuoteByIdApi,
  sendQuoteEmailApi,
  getQuotePdfApi,
} from "../../helpers/ApiManager";
import {
  CreateQuoteArgs,
  DeleteSectionApiArgs,
  getQuoteByIdParams,
  QuoteDetailsPayload,
  QuotesPayloadData,
  QuotesQueryParams,
  StatusPayload,
  UpdateQuoteArgs,
} from "types/quote.api";
import { ApiResponse, PaginatedQuoteSummaryPayload } from "types/ApiResponse";
import { ErrorHandler, SuccessToast, ToastFactory } from "utils/toast";
import { UpdateStatusApiArgs } from "types/setting.api";

export const getQuotes = createAsyncThunk<
  ApiResponse<PaginatedQuoteSummaryPayload<QuotesPayloadData>>, // return type
  QuotesQueryParams | undefined, // argument type
  { rejectValue: string } // rejected type
>("quotes/getQuotes", async (params, { rejectWithValue }) => {
  try {
    const response = await getQuotesApi(params);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to fetch");
  }
});

export const getQuoteById = createAsyncThunk<
  ApiResponse<QuoteDetailsPayload>, // return type
  number, // argument type
  { rejectValue: string } // rejected type
>("quotes/getQuoteById", async (quoteId, { rejectWithValue }) => {
  try {
    const response = await getQuoteByIdApi(quoteId);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to fetch quote by ID");
  }
});

export const getDuplicateQuoteById = createAsyncThunk<
  ApiResponse<QuoteDetailsPayload>, // return type
  getQuoteByIdParams, // argument type
  { rejectValue: string } // rejected type
>("quotes/getDuplicateQuoteById", async (params, { rejectWithValue }) => {
  try {
    const response = await getDuplicateQuoteByIdApi(params);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to fetch quote by ID");
  }
});

export const createQuote = createAsyncThunk<
  ApiResponse<QuoteDetailsPayload>, // return type
  CreateQuoteArgs, // argument type
  { rejectValue: string } // rejected type
>("quotes/createQuote", async (params, { rejectWithValue }) => {
  try {
    const response = await createQuoteApi(params);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to create quote");
  }
});

export const deleteQuote = createAsyncThunk<
  ApiResponse<number>, // return type
  number, // argument type
  { rejectValue: string } // rejected type
>("quotes/deleteQuote", async (quoteId, { rejectWithValue }) => {
  try {
    const response = await deleteQuoteApi(quoteId);
    response.payload = quoteId;
    ToastFactory("Quote", "Delete", true);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to delete");
  }
});

export const updateQuoteStatus = createAsyncThunk<
  ApiResponse<StatusPayload>, // return type
  UpdateStatusApiArgs, // argument type
  { rejectValue: string } // rejected type
>("quote/updateStatus", async (params, { rejectWithValue }) => {
  try {
    const response = await updateStatusApi(params);
    response.payload = {
      quote_id: params.quote_id,
      status: params.status,
    };
    // SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to edit");
  }
});

export const editQuote = createAsyncThunk<
  ApiResponse<QuoteDetailsPayload>, // return type
  UpdateQuoteArgs, // argument type
  { rejectValue: string } // rejected type
>("quotes/editQuote", async (params, { rejectWithValue }) => {
  try {
    const response = await editQuoteApi(params);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    console.log(err);
    return rejectWithValue(err?.message || "Failed to edit quote");
  }
});

export const deleteAttachment = createAsyncThunk<
  ApiResponse<number>,
  DeleteSectionApiArgs,
  { rejectValue: string }
>("quote/deleteAttachment", async (params, { rejectWithValue }) => {
  try {
    const response = await delteAttachmentApi(params);
    response.payload = params.attachment_id;
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to delete attachment");
  }
});

export const getQuotePdfPreview = createAsyncThunk<
  string, // return type: pdf preview html
  number, // argument type: quoteId
  { rejectValue: string } // rejected type
>("quotes/getQuotePdfPreview", async (quoteId, { rejectWithValue }) => {
  try {
    const response = await getQuotePdfApi({ quoteId, is_download: 0 });
    return response as string;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to load quote preview");
  }
});

export const sendQuoteEmail = createAsyncThunk<
  ApiResponse<null>, // return type
  { quoteId: number }, // argument type
  { rejectValue: string } // rejected type
>("quotes/sendQuoteEmail", async (params, { rejectWithValue }) => {
  try {
    const response = await sendQuoteEmailApi(params.quoteId);
    console.log(response);
    SuccessToast(response.message);
    return response;
  } catch (err: any) {
    console.log(err);
    ErrorHandler(err);
    return rejectWithValue(err?.message || "Failed to edit quote");
  }
});
